import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import api from '@/lib/api';
import { setAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { GraduationCap } from 'lucide-react';
import { DnaBackground } from '@/components/DnaBackground';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(email, password);
      setAuth(response.data.access_token, response.data.user);
      
      // Disparar evento customizado para recarregar permissões
      window.dispatchEvent(new CustomEvent('auth-change'));
      
      toast.success('Login realizado com sucesso!');
      
      // Redirecionar baseado no perfil
      const userRole = response.data.user?.role?.toLowerCase();
      if (userRole === 'teacher' || userRole === 'professor') {
        navigate('/equipe/portal');
      } else if (userRole === 'portaria') {
        navigate('/dnago/portaria');
      } else if (userRole === 'family' || userRole === 'guardian' || userRole === 'responsavel' || userRole === 'familia') {
        navigate('/familia');
      } else {
        // V4.50 · Portal Gestão DNA: se não é admin bypass mas tem perm portal_gestao.visualizar, direciona ao portal restrito.
        try {
          const perm = await api.get('/perm/me').then(r => r.data);
          const isAdmin = perm?.is_admin_bypass === true;
          const canPortalGestao = perm?.acoes?.['portal_gestao.visualizar'] === true;
          if (!isAdmin && canPortalGestao) {
            // V4.51 · Audit de login no Portal Gestão (fire-and-forget)
            api.post('/portal-gestao/audit', { action: 'login', page: '/gestao' }).catch(() => {});
            navigate('/gestao');
            return;
          }
        } catch (_) { /* fallback silencioso para o dashboard V2 */ }
        navigate('/v2/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="relative min-h-screen flex items-center justify-center p-4 safe-area-inset"
    >
      <DnaBackground />
      <Card className="relative z-10 w-full max-w-md backdrop-blur-xl bg-white/95 border-border/60 shadow-2xl mx-4" data-testid="login-card">
        <CardHeader className="space-y-2 sm:space-y-3 text-center px-4 sm:px-6 pt-6 sm:pt-8">
          <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg overflow-hidden bg-white">
            <img src="https://customer-assets.emergentagent.com/job_gestao-escolar-9/artifacts/kq81pgzg_NOVO%20DNA%20-%20logo.png" alt="DNACore" className="w-full h-full object-contain" />
          </div>
          <CardTitle className="text-2xl sm:text-3xl font-bold tracking-tight text-purple-900">DNACore</CardTitle>
          <CardDescription className="text-sm sm:text-base">#muitasmentes<br/>uma missão</CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-6 sm:pb-8">
          <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">E-mail</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 sm:h-12 text-base"
                autoComplete="email"
                autoCapitalize="none"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Senha</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 sm:h-12 text-base"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 sm:h-12 text-base font-semibold touch-target"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
          <p className="text-center text-xs sm:text-sm text-muted-foreground mt-4 sm:mt-6">
            Desenvolvido com ❤️ pela equipe DNA
          </p>
        </CardContent>
      </Card>
    </div>
  );
}